import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { Category } from '@eliadmin/core/src/components/categories/categories-page-new';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card } from '@eliadmin/core/src/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@eliadmin/core/src/components/ui/collapsible';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { arePathsEqual } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';
import React, { useState } from 'react';
import { LuChevronRight, LuMinus, LuPlus } from 'react-icons/lu';

export function AttributeCategory({ order }: { order: number }) {
  const { t } = useTranslations();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const categoryTree = useCategoryStore(state => state.categoryTree);

  const sortedCategoryTree = [...categoryTree].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0),
  );

  return (
    <Card className="bg-transparent">
      <Collapsible
        className={cn(`gap flex flex-col order-${order} px-4`, 'ea-attribute-category')}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <CollapsibleTrigger
          className={cn(
            'flex flex-row items-center gap-2',
            'ea-attribute-category__collapsible-trigger',
          )}
        >
          <span
            className={cn(
              'text-primary transition-transform duration-100 ease-linear',
              isOpen && 'rotate-90',
            )}
          >
            <LuChevronRight />
          </span>
          <span
            className={cn(
              'text-base font-semibold tracking-tight md:text-lg lg:text-xl',
              isOpen ? 'text-primary' : 'text-black',
            )}
          >
            {t('text176')}
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent className={cn('ml-6', 'ea-attribute-category__collapsible-trigger')}>
          {sortedCategoryTree.map(category => (
            <Category key={`category-${category.slug}`} category={category} />
          ))}
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

// const getAllChildren: any = (item: any) => {
//   let allChildren = [];

//   if (item.childs && Array.isArray(item.childs) && item.childs.length > 0) {
//     for (const child of item.childs) {
//       allChildren.push(child);
//       allChildren = allChildren.concat(getAllChildren(child));
//     }
//   }

//   return allChildren;
// };

function isCategoryOpen(currentCategory: Category & { slug: string }, category: CategoriesEntity) {
  // if my or child category is active
  if (arePathsEqual(category.slug, currentCategory.slug)) return true;
  if (category.childs?.some(child => isCategoryOpen(currentCategory, child))) return true;
  return false;
}

function Category({ category }: { category: CategoriesEntity }) {
  const pathname = usePathname();

  const currentCategory = useCategoryStore(state => state.category);

  const [isOpen, setIsOpen] = React.useState(
    arePathsEqual(category.slug, pathname) || isCategoryOpen(currentCategory, category),
  );

  const isActive = arePathsEqual(category.slug, currentCategory.slug);

  return (
    <Collapsible className="mt-2 flex flex-col" open={isOpen} onOpenChange={setIsOpen}>
      <div className="flex items-center justify-between">
        <Link
          href={category.slug ?? '#'}
          className={cn('text-sm', {
            'font-bold': isActive,
          })}
          prefetch={false}
        >
          {category.name}
        </Link>
        {!!category.childs?.length && (
          <CollapsibleTrigger asChild>
            <Button
              variant={'ghost'}
              size="icon-xs"
              onClick={() => setIsOpen((prev: any) => !prev)}
              className="ml-4 text-primary"
            >
              {isOpen ? <LuMinus className="h-4 w-4" /> : <LuPlus className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
        )}
      </div>
      {!!category.childs?.length && (
        <CollapsibleContent className="ml-4">
          {category.childs?.map(child => (
            <Category key={`${child.name}-${child.slug}`} category={child} />
          ))}
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}
